import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, IconButton } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CardTitleRow from './CardTitleRow';
import LoadErrorScreen from './LoadErrorScreen';
import './ItemCard.css';
import './ItemList.css';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/services/public`);
        if (!response.ok) throw new Error('Ошибка загрузки услуг');
        const data = await response.json();
        setServices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Ошибка загрузки услуг:', error);
        setLoadError(true);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const getPhotoUrl = (photo) => {
    if (!photo || typeof photo !== 'string') return null;
    const trimmed = photo.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('/')) return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
    return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
  };

  const getFirstPhoto = (service) => {
    const photos = service.photos;
    if (!photos || !Array.isArray(photos) || photos.length === 0) return null;
    for (const p of photos) {
      const url = getPhotoUrl(p);
      if (url) return url;
    }
    return null;
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.trim();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', px: 0.25, mx: 0, mt: 2 }}>
      <Grid container spacing={1} className="items-grid-container" sx={{ px: 0.5, overflowX: 'hidden', maxWidth: '100%' }}>
        {services && services.length > 0 ? (
          services.map((service, index) => {
            const photoUrl = getFirstPhoto(service) || 'https://via.placeholder.com/300';
            const isAboveFold = index < 6;
            const phone = formatPhone(service.contactNumber);
            const telHref = phone ? `tel:${phone.replace(/\s/g, '')}` : null;
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={service._id || service.serviceId} className="item-grid-item">
                <Card
                  component={Link}
                  to={`/services/${service.serviceId}`}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {telHref && (
                      <IconButton
                        component="a"
                        href={telHref}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          '&:hover': { backgroundColor: '#fff' },
                        }}
                        aria-label="Позвонить"
                      >
                        <PhoneIcon sx={{ color: '#2e7d32' }} />
                      </IconButton>
                    )}
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={service.name || 'Услуга'}
                        className="card-image"
                        loading={isAboveFold ? 'eager' : 'lazy'}
                        decoding="async"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300';
                        }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="service" entityId={service._id}>
                      {service.name}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      от {service.minPrice != null ? Number(service.minPrice).toLocaleString('ru-RU') : '—'} ₽
                    </Typography>
                    {phone && telHref && (
                      <Typography
                        component="a"
                        href={telHref}
                        variant="caption"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          mt: 0.5,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        📞 {phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Typography className="no-items-message">На данный момент услуг нет</Typography>
        )}
      </Grid>
    </Box>
  );
};

export default ServicesPage;
